import { getUserId, isBrowser } from './browserContext';
import {
    BRAZE_API_KEY,
    BRAZE_BASE_URL,
    CURRENT_ENV,
    ENVIRONMENT,
} from './config';

export type BrazeWebSDK = typeof import('@braze/web-sdk');

let braze: BrazeWebSDK | null = null;
let currentUserId: string | null = null;

export async function initBraze(): Promise<void> {
    if (!isBrowser()) return;

    try {
        const userId = getUserId();

        // If already initialized for this user, skip re-init
        if (currentUserId === userId) {
            return;
        }

        await loadAndConfigureBrazeForUser(userId); // If not yet initialized for any user, initialize the SDK
    } catch (error) {
        console.error('[Braze] Failed to initialize:', error);
        throw error;
    }
}

async function loadAndConfigureBrazeForUser(userId: string): Promise<void> {
    if (!braze) {
        braze = await import('@braze/web-sdk');
    }

    if (!currentUserId) {
        braze.initialize(BRAZE_API_KEY, {
            baseUrl: BRAZE_BASE_URL,
            enableLogging: CURRENT_ENV === ENVIRONMENT.DEVELOPMENT,
            allowUserSuppliedJavascript: true,
            enableSdkAuthentication: CURRENT_ENV === ENVIRONMENT.PRODUCTION,
        });
    }

    braze.changeUser(userId);
    braze.openSession();

    currentUserId = userId;
}

export async function getBrazeInstance(): Promise<BrazeWebSDK> {
    if (!braze) {
        try {
            await initBraze();
        } catch (error) {
            console.error('[Braze] Failed to initialize:', error);
            throw new Error(
                '[Braze] SDK failed to initialize. Please try again later.',
            );
        }
    }

    if (!braze) {
        throw new Error(
            '[Braze] SDK is unavailable after initialization. Please try again later.',
        );
    }

    return braze;
}
